import { formatDistanceToNowStrict } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, DropdownItem, DropdownMenu, Icon, Label, Popup } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, Dropdown, useCan } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { DMS_CAPABILITIES, KEYLABEL_LAYOUTS, STATUS_OVERRIDE_TYPES } from "models";
import { CustomerCommunicationButtons, DeskCommunication } from "modules/AppointmentDetails/components";
import KeyLabelModal from "modules/AppointmentDetails/components/IndicatorsAction/components/KeyLabelModal";
import {
  useAppointmentMoneyIndicatorMutation,
  useAppointmentShopIndicatorMutation,
  useAppointmentStarIndicatorMutation,
  useDmsRefreshMutation
} from "modules/AppointmentDetails/components/IndicatorsAction/hooks";
import "modules/AppointmentDetails/components/IndicatorsAction/IndicatorsAction.scss";
import { useAppointmentLocation, useDetachKioskLabelMutation } from "modules/AppointmentDetails/hooks";

import type { Appointment } from "models";

type Options = {
  key: string;
  icon: React.ReactNode;
  value: string;
  text?: string;
};

// TODO: we do this because tablet v7 cannot parse 'orange', remove when v7 is stopped
const orangeColor = "#f2711c";

const starOptions: Options[] = [
  { key: "green", icon: <Icon className="star" color={"green"} />, value: "green" },
  { key: "blue", icon: <Icon className="star" color={"blue"} />, value: "blue" },
  { key: "yellow", icon: <Icon className="star" color={"yellow"} />, value: "yellow" },
  { key: "red", icon: <Icon className="star" color={"red"} />, value: "red" }
];

type IndicatorsActionProps = {
  appointment: Appointment;
  showVat?: boolean;
  onSwitchShowVat?: () => void;
};

export const IndicatorsAction = ({ appointment, showVat = false, onSwitchShowVat }: IndicatorsActionProps) => {
  const { t } = useTranslation();

  const { mutate: updateMoneyIndicator } = useAppointmentMoneyIndicatorMutation(appointment.id);
  const { mutate: updateStarIndicator } = useAppointmentStarIndicatorMutation(appointment.id);
  const { mutate: updateShopIndicator } = useAppointmentShopIndicatorMutation(appointment.id);
  const { mutate: refreshDms } = useDmsRefreshMutation();
  const detachKioskMutation = useDetachKioskLabelMutation(appointment.id);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { user } = usePermanentRealTimeUser();
  const selectedLocation = useAppointmentLocation();

  const canUpdateAppointments = useCan("update", "appointments");
  const canUpdateStar = useCan("update", "appointments-star");
  const canUpdateShop = useCan("update", "appointments-shopping-cart");

  const cartOptions: Options[] = [
    { key: "green", icon: <Icon className="shopping cart" color={"green"} />, value: "green", text: `${t("v8_same_day", "Same day")}` },
    { key: "blue", icon: <Icon className="shopping cart" color={"blue"} />, value: "blue", text: `${t("v8_to_order", "To Order")}` },
    { key: orangeColor, icon: <Icon className="shopping cart orange-cart" />, value: orangeColor, text: `${t("v8_next_day", "Next Day")}` },
    { key: "red", icon: <Icon className="shopping cart" color={"red"} />, value: "red", text: `${t("v8_back_order", "Back Order")}` },
    { key: "yellow", icon: <Icon className="shopping cart yellow-shop" />, value: "yellow", text: `${t("v8_arrived", "Arrived")}` }
  ];

  const [carInShopPrintVisible, setCarInShopPrintVisible] = useState(false);
  const [keyLabelPrintSize, setKeyLabelPrintSize] = useState(selectedLocation?.key_label_layout_id || KEYLABEL_LAYOUTS.A4);

  const handleStarUpdate = (color: string) => updateStarIndicator({ is_star: true, is_star_color: color });

  const handleShopUpdate = (color: string) => updateShopIndicator({ is_shop: true, is_shop_color: color });

  const handleStarToggle = () => {
    if (appointment.is_star && appointment.is_star_color) updateStarIndicator({ is_star: false, is_star_color: "" });
    else updateStarIndicator({ is_star: true, is_star_color: "green" });
  };

  const handleShopToggle = () => {
    if (appointment.is_shop && appointment.is_shop_color) updateShopIndicator({ is_shop: false, is_shop_color: "" });
    else updateShopIndicator({ is_shop: true, is_shop_color: "green" });
  };

  const handleMoney = () => {
    if (!canUpdateAppointments) return;

    updateMoneyIndicator({
      is_money: !appointment.is_money
    });
  };

  const handleChangeKeyLabelPrintSize = (value: KEYLABEL_LAYOUTS) => {
    setKeyLabelPrintSize(value);
  };

  const handleCloseCarInShopPrintModal = () => setCarInShopPrintVisible(false);

  const handleDMSRefresh = () => {
    if (!selectedLocation?.dms_capability_ids?.includes(DMS_CAPABILITIES.Refresh)) return;

    refreshDms(appointment);
  };

  const canRefreshDMS = Boolean(selectedLocation?.dms_capability_ids?.includes(DMS_CAPABILITIES.Refresh));

  const onRequestConfirmCloseModal = () => setShowCancelModal(false);

  const handleDetachKiosk = () => {
    detachKioskMutation.mutate();
    setShowCancelModal(false);
  };

  const handleConfirmModal = () => setShowCancelModal(true);

  const cartOptionsWithPopup = cartOptions.map(option => ({
    key: option.value,
    text: <Popup content={option.text} trigger={option.icon} />,
    value: option.value
  }));

  return (
    <div className="IndicatorsAction">
      {appointment.kiosk_label_number && (
        <Label className="IndicatorsAction-kiosk-label" onClick={handleConfirmModal}>
          <Icon className="tag" />
          <span>{appointment.kiosk_label_number}</span>
        </Label>
      )}

      {!appointment.dms_nr.startsWith("claire_") && (
        <div className="dms-container">
          {canRefreshDMS && appointment.status_override !== STATUS_OVERRIDE_TYPES.Canceled && <Icon className="arrow rotate" color="green" onClick={handleDMSRefresh} />}
          <p>
            <strong>{t("v8_dms", "DMS")}</strong>
            {appointment.last_dms_update && formatDistanceToNowStrict(new Date(appointment.last_dms_update), { addSuffix: true })}
          </p>
        </div>
      )}

      {selectedLocation?.desk_check_in_enabled && user?.is_counter_tablet_user && <DeskCommunication appointment={appointment} />}

      <CustomerCommunicationButtons appointment={appointment} />

      <Button className="-appointment-status icon" size="small" onClick={() => setCarInShopPrintVisible(true)}>
        <Icon className="print limegreen" />
      </Button>

      <Button className="-appointment-status icon" size="small" onClick={handleMoney}>
        <Icon className={`dollar sign ${appointment.is_money ? "limegreen" : "grey"}`} disabled={!useCan("update", "appointments-billing")} />
      </Button>

      <Button.Group basic className="indicator-toggle">
        <Button className="indicator-toggle-button icon" size="small" disabled={!canUpdateStar} onClick={handleStarToggle}>
          <Icon className={`star ${appointment.is_star_color}`} />
        </Button>
        <Dropdown button className="status-dropdown caret-dropdown icon" icon="caret down" selectOnBlur={false} disabled={!canUpdateStar}>
          <DropdownMenu>
            {starOptions.map(star => (
              <DropdownItem onClick={() => handleStarUpdate(star.value)} key={star.key} icon={star.icon} selected={appointment.is_star_color === star.value} />
            ))}
          </DropdownMenu>
        </Dropdown>
      </Button.Group>

      <Button.Group basic className="indicator-toggle">
        <Popup
          disabled={!appointment.is_shop_color}
          content={cartOptions.find(option => option.key === appointment.is_shop_color)?.text}
          trigger={
            <Button className="indicator-toggle-button icon" size="small" disabled={!canUpdateShop} onClick={handleShopToggle}>
              <Icon
                className={`shopping cart ${appointment.is_shop_color === "yellow" ? "yellow-shop" : ""} ${appointment.is_shop_color === orangeColor ? "orange-cart" : appointment.is_shop_color}`}
              />
            </Button>
          }
        />
        <Dropdown button className="status-dropdown caret-dropdown icon" icon="caret down" selectOnBlur={false} disabled={!canUpdateShop}>
          <DropdownMenu>
            {cartOptionsWithPopup.map(cart => (
              <DropdownItem onClick={() => handleShopUpdate(cart.value)} key={cart.key} icon={cart.text} selected={appointment.is_shop_color === cart.value} />
            ))}
          </DropdownMenu>
        </Dropdown>
      </Button.Group>

      {selectedLocation?.dms_price_enabled && (
        <div className="container">
          <p>{t("v8_VAT", "VAT")}</p>
          <Checkbox toggle checked={showVat} onChange={onSwitchShowVat} disabled={!selectedLocation.include_vat} />
        </div>
      )}

      <KeyLabelModal
        appointment={appointment}
        handleChangeKeyLabelPrintSize={handleChangeKeyLabelPrintSize}
        keyLabelPrintSize={keyLabelPrintSize}
        handleCloseCarInShopPrintModal={handleCloseCarInShopPrintModal}
        carInShopPrintVisible={carInShopPrintVisible}
      />

      <CustomConfirm
        customTitle={t("v8_key_label_warning", "Key Label Warning")}
        type={CUSTOM_CONFIRM_TYPES.Warning}
        allowCustomReason
        isOpen={showCancelModal}
        customClass={"CancelAppointmentModal"}
        handleCancel={onRequestConfirmCloseModal}
        handleConfirm={handleDetachKiosk}
        isLoading={detachKioskMutation.isPending}
        confirmMsg={t("v8_confirm_disconnect_key_from_appointment", "Do you want to disconnect the key label from this WO?")}
        cancelButtonText={t("v8_no", "No")}
        confirmButtonText={t("v8_yes", "Yes")}
      />
    </div>
  );
};
